/**
 * Hooks for AI fix operations.
 */

import {
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { contentFeedbackService } from '../services/contentFeedbackService';
import type { AIFix, TriggerAIFixRequest } from '../types/contentFeedback';

const COMMENTS_QUERY_KEY = 'content-comments';
const AI_FIXES_QUERY_KEY = 'content-ai-fixes';
const CONTENT_ITEM_QUERY_KEY = 'blog-creation-content-item';

export function useTriggerAIFix(contentId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: TriggerAIFixRequest) =>
			contentFeedbackService.triggerAIFix(contentId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [COMMENTS_QUERY_KEY, contentId],
			});
			queryClient.invalidateQueries({
				queryKey: [AI_FIXES_QUERY_KEY, contentId],
			});
		},
	});
}

export function useApproveAIFix(contentId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (args: { fixId: string; editedContent?: Record<string, unknown> }) =>
			contentFeedbackService.approveAIFix(contentId, args.fixId, args.editedContent),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [COMMENTS_QUERY_KEY, contentId],
			});
			queryClient.invalidateQueries({
				queryKey: [AI_FIXES_QUERY_KEY, contentId],
			});
			// Refetch content so the editor reflects the applied fix in real-time
			queryClient.invalidateQueries({
				queryKey: [CONTENT_ITEM_QUERY_KEY, contentId],
			});
		},
	});
}

export function useRejectAIFix(contentId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (fixId: string) =>
			contentFeedbackService.rejectAIFix(contentId, fixId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [COMMENTS_QUERY_KEY, contentId],
			});
			queryClient.invalidateQueries({
				queryKey: [AI_FIXES_QUERY_KEY, contentId],
			});
		},
	});
}

export function useRevertAIFix(contentId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (fixId: string) =>
			contentFeedbackService.revertAIFix(contentId, fixId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [COMMENTS_QUERY_KEY, contentId],
			});
			queryClient.invalidateQueries({
				queryKey: [AI_FIXES_QUERY_KEY, contentId],
			});
		},
	});
}

export function useListAIFixes(contentId: string | null, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [AI_FIXES_QUERY_KEY, contentId],
		queryFn: () => contentFeedbackService.listAIFixes(contentId!),
		enabled: !!contentId && (options?.enabled !== false),
		staleTime: 5_000,
		refetchInterval: (query) => {
			// Only poll when there are pending/processing fixes
			const fixes = query.state.data as Array<{ status: string }> | undefined;
			if (!fixes?.length) return false;
			const hasActive = fixes.some(
				(f) => f.status === 'pending' || f.status === 'processing',
			);
			return hasActive ? 10_000 : false;
		},
	});
}

/**
 * Composite hook that wraps individual AI fix hooks into a single interface.
 * Provides triggerFix, approveFix, rejectFix, revertFix, and isProcessing.
 */
export function useAIFixes(contentId: string, _tenantId?: string) {
	const triggerMutation = useTriggerAIFix(contentId);
	const approveMutation = useApproveAIFix(contentId);
	const rejectMutation = useRejectAIFix(contentId);
	const revertMutation = useRevertAIFix(contentId);
	const fixesQuery = useListAIFixes(contentId);

	const isProcessing =
		triggerMutation.isPending ||
		approveMutation.isPending ||
		rejectMutation.isPending ||
		revertMutation.isPending ||
		(fixesQuery.data ?? []).some(
			(f: AIFix) => f.status === 'pending' || f.status === 'processing',
		);

	return {
		/** Trigger an AI fix for a comment. */
		triggerFix: (data: TriggerAIFixRequest) => triggerMutation.mutateAsync(data),
		/** Approve a pending fix, optionally with user-edited content. */
		approveFix: (fixId: string, editedContent?: Record<string, unknown>) =>
			approveMutation.mutateAsync({ fixId, editedContent }),
		/** Reject a pending fix. */
		rejectFix: (fixId: string) => rejectMutation.mutateAsync(fixId),
		/** Revert an applied fix. */
		revertFix: (fixId: string) => revertMutation.mutateAsync(fixId),
		/** Whether any fix operation is currently in progress. */
		isProcessing,
		/** List of all fixes for this content. */
		fixes: fixesQuery.data ?? [],
		/** Whether the fixes list is loading. */
		isLoading: fixesQuery.isLoading,
	};
}
